/**
 * The `booking` flow-spec kind, live appointment booking inside the Flow.
 *
 * ADR-0021 fixes the shape: service -> date -> time -> details -> confirm -> success,
 * with a data exchange on every step that needs live availability. Nothing about that
 * shape is a judgement call a model (or an owner) should be making, so it is not
 * expressible as free-form screens: a spec says `kind: "booking"` and the screens are
 * conferred here, exactly the way `compile` confers Footers, ids and the version literal.
 *
 * The screen ids and field names below are the contract with the runtime
 * (`booking/internal/DataExchangeService`, documented in `docs/launch/ws-C1.md`).
 * Changing one without the other breaks a live booking, so they are named constants
 * here and asserted in `__tests__/booking.test.js`.
 *
 * ## Why the date is a Dropdown and not a DatePicker
 *
 * The runtime answers with `available_dates: [{ id: "2026-09-06", title: "Sat 6 Sep" }]`
 *, an option list, because "which days do I have a free slot on" is a query the
 * schedule answers, not a range a calendar widget can express. A `DatePicker` binds to
 * `min-date`/`max-date`/`unavailable-dates` (millisecond strings), which cannot consume
 * `[{id,title}]` at all. A Dropdown can, so the customer picks from days that are
 * genuinely bookable rather than from a calendar that rejects most of what it offers.
 *
 * ## No clock, no randomness
 *
 * flow-core is executed by two hosts and its output is compared byte for byte, so the
 * `__example__` values below are fixed literals. They are what Meta renders in the
 * builder preview before an endpoint is attached, and what FlowGate's own preview
 * renders (`BOOKING_SAMPLE_DATA`), never live data.
 */

/** The spec kind marker. A spec without a `kind` is an ordinary form flow. */
export const BOOKING_KIND = 'booking';

/** Screen ids, fixed by the runtime contract. */
export const BOOKING_SCREENS = Object.freeze({
  SERVICE: 'SERVICE',
  DATE: 'DATE',
  TIME: 'TIME',
  DETAILS: 'DETAILS',
  CONFIRM: 'CONFIRM',
  SUCCESS: 'SUCCESS',
});

export const BOOKING_SCREEN_IDS = Object.freeze(Object.values(BOOKING_SCREENS));

/** Field names, fixed by the runtime contract (see ws-C1 "Screen contract with C2"). */
export const BOOKING_FIELDS = Object.freeze({
  serviceId: 'service_id',
  date: 'date',
  time: 'time',
  name: 'customer_name',
  phone: 'customer_phone',
  notes: 'notes',
});

/**
 * What is wrong with a booking spec, if anything.
 *
 * Only one thing can be: the services. Everything else about a booking flow is conferred, so
 * there is nothing else for an author to get wrong, and a booking flow with no services is
 * the one failure a repair turn can actually fix (the model was told to look the business up;
 * this sends it back to do that).
 *
 * @returns {string[]} errors, empty when the spec is usable
 */
export const bookingSpecErrors = (spec) => {
  if (spec?.kind !== BOOKING_KIND) return [];
  const services = spec.booking?.services;
  const usable = Array.isArray(services)
    && services.some((service) => service && service.id && (service.title || service.name));
  return usable
    ? []
    : ['A booking flow needs the services this business offers: set "booking" to '
       + '{ "services": [ { "id": "mens_haircut", "title": "Men\'s haircut", "duration_minutes": 30 } ] }. '
       + 'Use the business\'s real services if you can find them, otherwise one sensible default.'];
};

/** True when this spec asks for the fixed booking flow rather than free-form screens. */
export const isBookingSpec = (spec) => spec?.kind === BOOKING_KIND;

const OPTION_LIST_SCHEMA = {
  type: 'array',
  items: {
    type: 'object',
    properties: { id: { type: 'string' }, title: { type: 'string' } },
  },
};

/** Sample options, used for `__example__` and for FlowGate's own preview. */
export const BOOKING_SAMPLE_DATA = Object.freeze({
  services: Object.freeze([
    { id: 'service_a', title: 'Appointment' },
  ]),
  available_dates: Object.freeze([
    { id: '2026-09-05', title: 'Fri 5 Sep' },
    { id: '2026-09-06', title: 'Sat 6 Sep' },
  ]),
  slots: Object.freeze([
    { id: '09:00', title: '09:00' },
    { id: '09:30', title: '09:30' },
  ]),
});

/** `[{id,name}]` or `[{id,title}]` in, the `[{id,title}]` the runtime speaks out. */
const serviceOptions = (services) => {
  const options = (services || [])
    .map((service) => ({
      id: String(service?.id ?? '').trim(),
      title: String(service?.title ?? service?.name ?? '').trim(),
    }))
    .filter((option) => option.id !== '' && option.title !== '');
  return options.length > 0 ? options : BOOKING_SAMPLE_DATA.services.map((s) => ({ ...s }));
};

const optionList = (example) => ({ ...OPTION_LIST_SCHEMA, __example__: example });
const text = (example) => ({ type: 'string', __example__: example });

/**
 * The fixed booking screens, as a FlowSpec `screens` array.
 *
 * `expandSpec` runs this through exactly the same expansion any other spec gets, ids
 * are minted, defaults filled, and `ensureFootersOnAllScreens` leaves these Footers
 * alone because every screen already has one.
 *
 * @param {{ services?: Array, business_name?: string, ask_notes?: boolean }} [booking]
 */
export const bookingScreens = (booking = {}) => {
  const services = serviceOptions(booking.services);
  const dates = BOOKING_SAMPLE_DATA.available_dates.map((d) => ({ ...d }));
  const slots = BOOKING_SAMPLE_DATA.slots.map((s) => ({ ...s }));
  const askNotes = booking.ask_notes !== false;
  const who = booking.business_name ? String(booking.business_name).trim() : '';

  const F = BOOKING_FIELDS;
  const S = BOOKING_SCREENS;

  const details = [
    {
      type: 'TextInput',
      name: F.name,
      label: 'Your name',
      'input-type': 'text',
      required: true,
    },
    {
      type: 'TextInput',
      name: F.phone,
      label: 'Phone',
      'input-type': 'phone',
      required: false,
      'helper-text': 'So we can reach you if anything changes.',
    },
  ];
  if (askNotes) {
    details.push({
      type: 'TextArea',
      name: F.notes,
      label: 'Anything else?',
      required: false,
    });
  }

  return [
    {
      // Skipped at runtime when the business offers exactly one service: INIT answers
      // with DATE instead, and `service_id` comes pre-filled. The screen still exists,
      // because a second service can be added without republishing the flow.
      id: S.SERVICE,
      title: 'Choose a service',
      data: {
        services: optionList(services),
        available_dates: optionList(dates),
        service_id: text(''),
      },
      components: [
        { type: 'TextHeading', text: who ? `Book with ${who}` : 'Book an appointment' },
        {
          type: 'RadioButtonsGroup',
          name: F.serviceId,
          label: 'What would you like?',
          required: true,
          'data-source-mode': 'dynamic',
          'data-source-ref': '${data.services}',
        },
        {
          type: 'Footer',
          label: 'Continue',
          'on-click-action': {
            name: 'data_exchange',
            payload: { service_id: '${form.' + F.serviceId + '}' },
          },
        },
      ],
    },
    {
      id: S.DATE,
      title: 'Choose a day',
      data: {
        services: optionList(services),
        available_dates: optionList(dates),
        service_id: text(''),
      },
      components: [
        { type: 'TextHeading', text: 'Pick a day' },
        {
          type: 'Dropdown',
          name: F.date,
          label: 'Day',
          required: true,
          'data-source-mode': 'dynamic',
          'data-source-ref': '${data.available_dates}',
        },
        {
          type: 'Footer',
          label: 'See times',
          'on-click-action': {
            name: 'data_exchange',
            payload: {
              date: '${form.' + F.date + '}',
              service_id: '${data.service_id}',
            },
          },
        },
      ],
    },
    {
      id: S.TIME,
      title: 'Choose a time',
      data: {
        slots: optionList(slots),
        date: text('2026-09-06'),
        service_id: text(''),
        // Sent only when something went wrong (a slot taken between loading and
        // submitting, a missing answer). Meta allows a declared property to be omitted,
        // so the happy path simply leaves it out and this renders empty.
        error_message: text(''),
      },
      components: [
        { type: 'TextBody', text: '${data.error_message}' },
        {
          type: 'RadioButtonsGroup',
          name: F.time,
          label: 'What time suits you?',
          required: true,
          'data-source-mode': 'dynamic',
          'data-source-ref': '${data.slots}',
        },
        {
          type: 'Footer',
          label: 'Continue',
          'on-click-action': {
            name: 'navigate',
            next: { type: 'screen', name: S.DETAILS },
          },
        },
      ],
    },
    {
      id: S.DETAILS,
      title: 'Your details',
      components: [
        ...details,
        {
          type: 'Footer',
          label: 'Review',
          'on-click-action': {
            name: 'navigate',
            next: { type: 'screen', name: S.CONFIRM },
            // What the review screen shows. A navigate payload is how a value reaches the next
            // screen's `data`, and `data` is the ONLY thing a text component will interpolate:
            // a reference to another screen's form, printed as text, is shown to the customer
            // verbatim (2026-09-20, Meta's validator does not even resolve it).
            payload: {
              chosen_date: '${screen.' + S.DATE + '.form.' + F.date + '}',
              chosen_time: '${screen.' + S.TIME + '.form.' + F.time + '}',
              chosen_name: '${form.' + F.name + '}',
            },
          },
        },
      ],
    },
    {
      id: S.CONFIRM,
      title: 'Confirm',
      // Filled by the navigate payload on the DETAILS screen. Declared here because a screen
      // may only read data it declares, and the examples are what the builder's preview shows.
      data: {
        chosen_date: text('Thu 12 Jun'),
        chosen_time: text('09:30'),
        chosen_name: text('Sam Taylor'),
      },
      components: [
        { type: 'TextHeading', text: 'Check and confirm' },
        { type: 'TextBody', text: 'Day: ${data.chosen_date}' },
        { type: 'TextBody', text: 'Time: ${data.chosen_time}' },
        { type: 'TextBody', text: 'Name: ${data.chosen_name}' },
        {
          type: 'Footer',
          label: 'Confirm booking',
          'on-click-action': {
            name: 'data_exchange',
            // Everything the runtime needs to create the booking, in one submit.
            // `service_id` comes off the TIME screen's data because the SERVICE screen
            // is skipped for a single-service business and its form would be empty.
            payload: {
              service_id: '${screen.TIME.data.service_id}',
              date: '${screen.DATE.form.' + F.date + '}',
              time: '${screen.TIME.form.' + F.time + '}',
              name: '${screen.DETAILS.form.' + F.name + '}',
              phone: '${screen.DETAILS.form.' + F.phone + '}',
              ...(askNotes ? { notes: '${screen.DETAILS.form.' + F.notes + '}' } : {}),
            },
          },
        },
      ],
    },
    {
      id: S.SUCCESS,
      title: 'Booked',
      terminal: true,
      data: {
        // Meta's terminal-screen convention: the endpoint puts whatever should reach the
        // business into `extension_message_response`, and the Footer returns it whole.
        extension_message_response: {
          type: 'object',
          properties: { params: { type: 'object' } },
          __example__: {
            params: {
              flow_token: 'unused',
              booking_id: '00000000-0000-0000-0000-000000000000',
              starts_at: '2026-09-06T09:00:00Z',
            },
          },
        },
      },
      components: [
        { type: 'TextHeading', text: "You're booked" },
        { type: 'TextBody', text: 'We have sent the details to your WhatsApp.' },
        {
          type: 'Footer',
          label: 'Done',
          // Empty on purpose, as in Meta's own terminal-screen sample: what reaches the
          // business is `extension_message_response`, which the endpoint returned, not this.
          // It used to be the string `${data.extension_message_response}`, "return that whole"
          //, which Meta refuses outright (payload must be an object), so every booking flow
          // deployed INVALID until 2026-09-23. `stampFlowId` fills in the flow id here.
          'on-click-action': {
            name: 'complete',
            payload: {},
          },
        },
      ],
    },
  ];
};

/**
 * The JSON Schema fragment describing the `booking` object a generating model writes.
 * Lives here rather than in `spec.js` so the kind is one file.
 */
export const bookingGenerationSchema = () => ({
  type: 'object',
  additionalProperties: false,
  required: ['services'],
  description:
    'Required when kind is "booking". The services the business offers, in their own words.',
  properties: {
    business_name: { type: 'string', maxLength: 60 },
    services: {
      type: 'array',
      minItems: 1,
      maxItems: 10,
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['id', 'title'],
        properties: {
          id: {
            type: 'string',
            pattern: '^[a-z][a-z0-9_]*$',
            description: 'snake_case, e.g. mens_haircut',
          },
          title: { type: 'string', maxLength: 40 },
          duration_minutes: {
            type: 'number',
            description: 'How long the appointment takes. 30 if you cannot tell.',
          },
        },
      },
    },
    ask_notes: {
      type: 'boolean',
      description: 'Whether to ask for free-text notes on the details screen. Default true.',
    },
  },
});

/**
 * True when a compiled Flow JSON is FlowGate's booking flow.
 *
 * The backend asks this at save/publish to decide whether the Meta flow needs an
 * `endpoint_uri` and an endpoint key. It is deliberately structural, the screen ids ARE
 * the runtime contract, so a flow that carries all six is one the booking runtime can
 * serve, however it got there (generated, imported, or edited by hand).
 *
 * @param {Object} flowJson
 */
export const isBookingFlowJson = (flowJson) => {
  const ids = ((flowJson && flowJson.screens) || []).map((screen) => screen && screen.id);
  return BOOKING_SCREEN_IDS.every((id) => ids.includes(id));
};

/**
 * A booking spec rebound to the services the business has actually configured.
 *
 * A booking flow's live service list comes from `${data.services}`, which the runtime reads off
 * the schedule, the model's services only ever become the `__example__` values. So when the
 * owner has already set their services up, the model's guesses are not merely unnecessary, they
 * are a preview of something that will never happen: the builder shows "Men's haircut" while the
 * customer is offered "Full groom, large dog". Rebinding here makes the preview the truth, and
 * leaves a business with nothing configured exactly as it was.
 *
 * Pure and total: a spec that is not a booking spec, or an empty list, comes back untouched, so
 * the caller never has to ask first.
 *
 * @param {Object} spec the generated flow spec
 * @param {Array<{id: string, title?: string, name?: string, duration_minutes?: number}>} services
 *   the configured services, in the order the owner put them in
 * @returns {Object} the spec to compile
 */
export const withConfiguredServices = (spec, services) => {
  if (!isBookingSpec(spec)) return spec;
  const usable = (services || [])
    .map((service) => ({
      id: String(service?.id ?? '').trim(),
      title: String(service?.title ?? service?.name ?? '').trim(),
      ...(service?.duration_minutes ? { duration_minutes: service.duration_minutes } : {}),
    }))
    .filter((service) => service.id !== '' && service.title !== '');
  if (usable.length === 0) return spec;
  return { ...spec, booking: { ...(spec.booking || {}), services: usable } };
};

/**
 * The services a booking flow was built around, read off the SERVICE screen's data example
 * (see `optionList` in `bookingScreens` above, that example is the only place a compiled flow
 * carries them at rest; at runtime the list comes from the data-exchange endpoint instead).
 *
 * <p>Backend needs this to reconcile a newly-published booking flow against the schedule it will
 * run against (seeding services the owner asked for but never opened Settings to add), it used
 * to walk the screen id and the `data.services.__example__` path itself
 * ({@code BookingFlowPublisher.servicesOf}), a second definition of a shape only this file should
 * define. This is the one place; the backend now just calls it.
 *
 * @param {Object} flowJson compiled Flow JSON
 * @returns {Array<{id: string, title: string}>}
 */
export const bookingServices = (flowJson) => {
  const screens = (flowJson && flowJson.screens) || [];
  const serviceScreen = screens.find((screen) => screen && screen.id === BOOKING_SCREENS.SERVICE);
  const options = serviceScreen?.data?.services?.__example__;
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => ({ id: String(option?.id ?? '').trim(), title: String(option?.title ?? '').trim() }))
    .filter((option) => option.id !== '' && option.title !== '');
};
